import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  allocationsApi,
  type AllocationCellUpdate,
  type AllocationGridRow,
  type AllocationVersion,
} from "@/services/api/hr";

export const Route = createFileRoute("/portal/hr/allocations/")({
  component: AllocationsPage,
});

function formatPct(value: number | undefined): string {
  if (value === undefined || value === null) return "—";
  return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

// A row is one (employee, payroll item) split at a specific pair of allocation
// dimensions — the key MUST include those ids, or edits mirror across the
// sibling cost-centre rows of the same employee+item and corrupt the split.
const cellKey = (row: AllocationGridRow, period: string) =>
  `${row.employeeId}|${row.payrollItemId}|${row.allocation01Id ?? ""}|${row.allocation02Id ?? ""}|${period}`;

const isEditable = (row: AllocationGridRow) =>
  row.employeeId != null && row.payrollItemId != null;

function AllocationsPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();
  const [version, setVersion] = useState("");
  const [milestone, setMilestone] = useState("");
  // Pending cell edits, keyed employeeId|payrollItemId|period → raw input value.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset the selection the render org changes, BEFORE the query observer sees a
  // stale key — an effect would fire one foreign-version fetch against the new
  // org first.
  const [prevOrg, setPrevOrg] = useState(orgId);
  if (prevOrg !== orgId) {
    setPrevOrg(orgId);
    setVersion("");
    setMilestone("");
    setEdits({});
  }

  const grid = useQuery({
    queryKey: ["hr-allocations", orgId, version, milestone],
    queryFn: () => allocationsApi.grid(token, version, milestone),
    enabled: !!token && !!orgId,
  });

  // Seed the selectors from the API's auto-selected default (the no-version call
  // returns the lookups plus the first version/milestone).
  useEffect(() => {
    if (version === "" && grid.data?.version) {
      setVersion(grid.data.version);
      setMilestone(grid.data.milestone || "Baseline");
    }
  }, [grid.data, version]);

  const versions: AllocationVersion[] = grid.data?.lookups?.versions ?? [];
  const milestones =
    versions.find((v) => v.name === version)?.milestones ?? [];
  const data = grid.data;
  const rows = data?.rows ?? [];
  const periods = data?.periods ?? [];
  const dim1 = data?.dimension1_label ?? "Allocation 1";
  const dim2 = data?.dimension2_label ?? "Allocation 2";

  const onVersionChange = (next: string) => {
    setVersion(next);
    // Empty when the version has no milestones → let the API pick its default.
    setMilestone(versions.find((v) => v.name === next)?.milestones[0]?.name ?? "");
    setEdits({});
  };

  const onMilestoneChange = (next: string) => {
    setMilestone(next);
    setEdits({});
  };

  // Only edits that map to a currently-visible row/period get saved; return the
  // keys too so dirtyCount and the payload never disagree (and so onSuccess can
  // clear exactly what it saved).
  const buildCells = (): { cells: AllocationCellUpdate[]; keys: string[] } => {
    const cells: AllocationCellUpdate[] = [];
    const keys: string[] = [];
    for (const row of rows) {
      if (row.employeeId == null || row.payrollItemId == null) continue;
      for (const p of periods) {
        const key = cellKey(row, p);
        if (!(key in edits)) continue;
        const raw = edits[key].trim();
        const num = Number(raw);
        cells.push({
          employee_id: row.employeeId,
          payroll_item_id: row.payrollItemId,
          allocation01_id: row.allocation01Id,
          allocation02_id: row.allocation02Id,
          period: `${p}-01`, // grid labels are YYYY-MM; the API cell wants a date
          // Empty = "unallocated" (null), not an explicit 0%.
          percent: raw === "" ? null : Number.isFinite(num) ? num : null,
        });
        keys.push(key);
      }
    }
    return { cells, keys };
  };

  const pending = buildCells();
  const dirtyCount = pending.cells.length;

  const save = useMutation({
    mutationFn: async () => {
      const { cells, keys } = buildCells();
      await allocationsApi.save(token, {
        version,
        milestone: milestone || data?.milestone || "Baseline",
        cells,
      });
      return keys;
    },
    onSuccess: async (savedKeys) => {
      toast.success("Allocations saved");
      // Refetch BEFORE clearing edits so the inputs don't flash back to the
      // stale pre-save values while the GET is in flight.
      await queryClient.invalidateQueries({ queryKey: ["hr-allocations"] });
      setEdits((prev) => {
        const next = { ...prev };
        for (const k of savedKeys) delete next[k];
        return next;
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  // Sum-to-100 guard: a row is one cost-centre split of an (employee, item), so
  // each (employee, item, period) column should total 100% across its rows.
  // Only flag columns the user has actually edited — pre-existing bad data isn't
  // this save's concern.
  const groupKeyOf = (row: AllocationGridRow, period: string) =>
    `${row.employeeId}|${row.payrollItemId}|${period}`;
  const effectivePct = (row: AllocationGridRow, period: string): number => {
    const key = cellKey(row, period);
    const raw = key in edits ? edits[key] : String(row.values[period] ?? 0);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };
  const groupSums = new Map<string, number>();
  const editedGroups = new Set<string>();
  for (const row of rows) {
    if (!isEditable(row)) continue;
    for (const p of periods) {
      const gk = groupKeyOf(row, p);
      groupSums.set(gk, (groupSums.get(gk) ?? 0) + effectivePct(row, p));
      if (cellKey(row, p) in edits) editedGroups.add(gk);
    }
  }
  const offGroups = new Set<string>();
  for (const gk of editedGroups) {
    // Round to 2dp before comparing so FP noise (e.g. 33.33×3) doesn't flag
    // one on-paper total but not an identical one.
    const sum = Math.round((groupSums.get(gk) ?? 0) * 100) / 100;
    if (Math.abs(sum - 100) > 0.01) offGroups.add(gk);
  }

  const attemptSave = () => {
    if (offGroups.size > 0) setConfirmOpen(true);
    else save.mutate();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900">Cost Allocations</h1>
        <p className="mt-2 text-lg text-slate-600">
          How each employee&apos;s payroll cost is split across cost centres, by
          version and milestone.
        </p>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="text-2xl font-semibold text-slate-900">Allocation grid</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Version</Label>
              <Select
                value={version}
                onValueChange={onVersionChange}
                disabled={versions.length === 0}
              >
                <SelectTrigger className="w-48" data-testid="allocation-version">
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((v) => (
                    <SelectItem key={v.id} value={v.name}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Milestone</Label>
              <Select
                value={milestone}
                onValueChange={onMilestoneChange}
                disabled={milestones.length === 0}
              >
                <SelectTrigger className="w-48" data-testid="allocation-milestone">
                  <SelectValue placeholder="Select milestone" />
                </SelectTrigger>
                <SelectContent>
                  {milestones.map((m) => (
                    <SelectItem key={m.id} value={m.name}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={attemptSave}
              disabled={dirtyCount === 0 || save.isPending}
              data-testid="allocation-save"
            >
              {save.isPending
                ? "Saving…"
                : dirtyCount > 0
                  ? `Save ${dirtyCount} change${dirtyCount > 1 ? "s" : ""}`
                  : "Save"}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {grid.isLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {grid.isError && (
            <p className="text-sm text-red-600">Failed to load allocations.</p>
          )}
          {data?.error && (
            <p className="text-sm text-red-600">{data.error}</p>
          )}
          {data && versions.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No allocation versions yet.
            </p>
          )}
          {data && data.status && (
            <p className="mb-3 text-sm text-muted-foreground">
              Status:{" "}
              <span className="font-medium text-foreground">{data.status}</span>
            </p>
          )}
          {offGroups.size > 0 && (
            <p className="mb-3 text-sm text-amber-600" data-testid="allocation-sum-warning">
              {offGroups.size} column{offGroups.size > 1 ? "s" : ""}{" "}
              {offGroups.size === 1 ? "doesn't" : "don't"} total 100% — check the
              highlighted cells before saving.
            </p>
          )}
          {data && !data.error && versions.length > 0 && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No allocations for this version and milestone.
            </p>
          )}
          {data && rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Payroll item</TableHead>
                  <TableHead>{dim1}</TableHead>
                  <TableHead>{dim2}</TableHead>
                  {periods.map((p) => (
                    <TableHead key={p} className="text-right">
                      {p}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, idx) => {
                  const editable = isEditable(row);
                  return (
                    <TableRow key={idx} data-testid={`allocation-row-${idx}`}>
                      <TableCell className="font-medium">
                        {row.employeeName ?? row.employeeCode ?? "—"}
                      </TableCell>
                      <TableCell>{row.payrollItemName ?? "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {row.allocation01Name ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {row.allocation02Name ?? "—"}
                      </TableCell>
                      {periods.map((p) => {
                        if (!editable) {
                          return (
                            <TableCell key={p} className="text-right tabular-nums">
                              {formatPct(row.values[p])}
                            </TableCell>
                          );
                        }
                        const key = cellKey(row, p);
                        const value =
                          key in edits ? edits[key] : String(row.values[p] ?? 0);
                        const off = offGroups.has(groupKeyOf(row, p));
                        return (
                          <TableCell key={p} className="text-right">
                            <Input
                              type="number"
                              step="0.01"
                              value={value}
                              onChange={(e) =>
                                setEdits((prev) => ({ ...prev, [key]: e.target.value }))
                              }
                              // Stop the mouse wheel from silently changing values
                              // while scrolling the grid.
                              onWheel={(e) => e.currentTarget.blur()}
                              className={cn(
                                "h-8 w-24 text-right tabular-nums",
                                off && "border-red-500 focus-visible:ring-red-500",
                              )}
                              data-testid={`cell-${idx}-${p}`}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Some columns don&apos;t total 100%</AlertDialogTitle>
            <AlertDialogDescription>
              {offGroups.size} edited column{offGroups.size > 1 ? "s" : ""}{" "}
              {offGroups.size === 1 ? "doesn't" : "don't"} sum to 100% across cost
              centres. Save anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                save.mutate();
              }}
              disabled={save.isPending}
              data-testid="allocation-save-confirm"
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
